import { FormattedMessage, useIntl } from "react-intl";
import Alert from "reactstrap/lib/Alert";
import Col from "reactstrap/lib/Col";
import Row from "reactstrap/lib/Row";
import Button from "reactstrap/lib/Button";
import Table from "reactstrap/lib/Table";
import { formatDate } from "../../Utilities/dateUtils";
import {
  BarRow,
  DayBars,
  FindingTabs,
  PanelCard,
  StatCard,
  TrendLine,
  findingsForTab,
  formatCompact,
  formatNumber,
} from "./DailyAnalysisParts";

const ActiveUserTypes = {
  First: ["Teacher", "Student"],
  Staff: ["Admin", "Superuser", "Analytics"],
};

const activeUserRank = (type) => {
  const index = ActiveUserTypes.First.indexOf(type.userType);
  return index === -1 ? ActiveUserTypes.First.length : index;
};

// The tabs each show one kind of finding. This page shows how they are distributed,
// which is the one view none of them can give, and sends the reader on from there.
function DailyAnalysisOverview({ data, onSelectTab }) {
  const intl = useIntl();
  const summary = data.summary || {};
  const activeUsers = data.activeUsers;
  const typeCounts = activeUsers?.types || [];
  const peopleCounts = typeCounts
    .filter((type) => !ActiveUserTypes.Staff.includes(type.userType))
    .sort((a, b) => activeUserRank(a) - activeUserRank(b));
  const staffCounts = typeCounts.filter((type) =>
    ActiveUserTypes.Staff.includes(type.userType),
  );
  const dailyByType = activeUsers?.daily?.byType;
  const sumCounts = (counts) =>
    counts.every((count) => Number.isFinite(count))
      ? counts.reduce((sum, count) => sum + count, 0)
      : null;
  const allUsers = {
    active: sumCounts(typeCounts.map((type) => dailyByType?.[type.userType])),
    total: sumCounts(typeCounts.map((type) => type.total)),
    neverSeen: sumCounts(typeCounts.map((type) => type.neverSeen)),
  };
  const activeUserRows = [
    ...peopleCounts.map((type) => ({
      userType: type.userType,
      active: Number.isFinite(dailyByType?.[type.userType])
        ? dailyByType[type.userType]
        : null,
      total: type.total,
      neverSeen: type.neverSeen,
    })),
    ...(staffCounts.length > 0
      ? [
          {
            userType: "Admin",
            active: sumCounts(
              staffCounts.map((type) => dailyByType?.[type.userType]),
            ),
            total: sumCounts(staffCounts.map((type) => type.total)),
            neverSeen: sumCounts(staffCounts.map((type) => type.neverSeen)),
          },
        ]
      : []),
  ];
  const dailySeries = activeUsers?.dailySeries || [];
  const trendLabels = dailySeries.map((point) => formatDate(point.date, "d/M"));
  const trendSeries = peopleCounts
    .map((type, index) => ({
      id: type.userType,
      name: intl.formatMessage({ id: type.userType }),
      colorIndex: index + 1,
      values: dailySeries.map((point) =>
        Number.isFinite(point.byType?.[type.userType])
          ? point.byType[type.userType]
          : null,
      ),
    }))
    .filter((series) => series.values.some(Number.isFinite));
  const showDailyTrend = trendSeries.some(
    (series) => series.values.filter(Number.isFinite).length > 1,
  );
  const trends = data.trends || {};
  const usage = data.usage || {};
  const shortHistory =
    typeof data.baselineDays === "number" && data.baselineDays < 14;

  const hours = (trends.requestsByHour || []).map((count, hour) => ({
    label: String(hour).padStart(2, "0"),
    count,
  }));
  const peakHour =
    hours.length > 0
      ? hours.reduce((a, b) => (b.count > a.count ? b : a), hours[0])
      : null;

  const days = (trends.requestsByDay || []).map((day) => ({
    label: formatDate(day.date, "d/M"),
    count: day.count,
  }));
  const changeFromPrevious = summary.requestsPrevious
    ? ((summary.requests - summary.requestsPrevious) /
        summary.requestsPrevious) *
      100
    : null;

  const moduleStartDays = (trends.moduleStartsByDay || []).map((day) => ({
    label: formatDate(day.date, "d/M"),
    count: day.count,
  }));
  const moduleStartsOnDay = (trends.moduleStartsByDay || []).find(
    (day) => day.date === data.date,
  );
  const moduleStartHours = (trends.moduleStartsByHour || []).map(
    (count, hour) => ({
      label: String(hour).padStart(2, "0"),
      count,
    }),
  );
  const moduleStartPeakHour = moduleStartHours.reduce(
    (a, b) => (b.count > a.count ? b : a),
    moduleStartHours[0],
  );

  const perTab = FindingTabs.map((tab) => {
    const mine = findingsForTab(data, tab.id);
    return {
      ...tab,
      crit: mine.filter((f) => f.severity === "crit").length,
      warn: mine.filter((f) => f.severity !== "crit").length,
    };
  });

  // trafficSplit holds fractions (0.237) and is shown as percentages.
  const rawSplit = usage.trafficSplit;
  const split = rawSplit
    ? [
        {
          id: "content",
          labelId: "DailyAnalysisContentAndStatic",
          value: (rawSplit.content || 0) * 100,
        },
        {
          id: "api",
          labelId: "DailyAnalysisTrafficApi",
          value: (rawSplit.api || 0) * 100,
        },
        {
          id: "other",
          labelId: "DailyAnalysisOther",
          value: (rawSplit.other || 0) * 100,
        },
      ]
    : null;
  const splitMax = split ? Math.max(...split.map((s) => s.value)) : 0;

  return (
    <div>
      {/* A history shorter than fourteen days overstates the number of "new" anomalies.
          The banner disappears on its own once baselineDays catches up. */}
      {shortHistory ? (
        <Alert color="warning" className="da-banner">
          <FormattedMessage
            id="DailyAnalysisShortBaseline"
            values={{ days: data.baselineDays }}
          />
        </Alert>
      ) : (
        ""
      )}
      {data.headline ? (
        <p className="da-headline">
          <span>{data.headline}</span>
        </p>
      ) : (
        ""
      )}
      <Row className="da-equal">
        <Col md sm="6">
          <StatCard
            labelId="DailyAnalysisRequests"
            value={formatCompact(summary.requests)}
            note={
              summary.requestsBaselineMedian ? (
                <FormattedMessage
                  id="DailyAnalysisMedian"
                  values={{
                    value: formatCompact(summary.requestsBaselineMedian),
                  }}
                />
              ) : (
                ""
              )
            }
          />
        </Col>
        {moduleStartsOnDay ? (
          <Col md sm="6">
            <StatCard
              labelId="DailyAnalysisModuleStarts"
              value={formatNumber(moduleStartsOnDay.count)}
              note={
                <>
                  {typeof summary.finalExamStarts === "number" ? (
                    <div>
                      <FormattedMessage
                        id="DailyAnalysisModuleStartsFinalExams"
                        values={{
                          count: formatNumber(summary.finalExamStarts),
                        }}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                  {typeof summary.moduleStartsWeekdayMedian === "number" ? (
                    <div>
                      <FormattedMessage
                        id="DailyAnalysisModuleStartsWeekdayMedian"
                        values={{
                          value: formatNumber(
                            summary.moduleStartsWeekdayMedian,
                          ),
                          change: `${summary.moduleStartsWeekdayMedianChange > 0 ? "+" : ""}${formatNumber(summary.moduleStartsWeekdayMedianChange)}`,
                        }}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                </>
              }
              tone={summary.moduleStartsDeviates ? "crit" : ""}
            />
          </Col>
        ) : (
          ""
        )}
        <Col md sm="6">
          <StatCard
            labelId="DailyAnalysisServerErrors"
            value={formatNumber(summary.errors5xx)}
          />
        </Col>
        <Col md sm="6">
          <StatCard
            labelId="DailyAnalysisUnauthorized"
            value={formatCompact(summary.errors401)}
            note={
              summary.requests ? (
                <FormattedMessage
                  id="DailyAnalysisShareOfTraffic"
                  values={{
                    value: (
                      (summary.errors401 / summary.requests) *
                      100
                    ).toLocaleString("sv-SE", { maximumFractionDigits: 1 }),
                  }}
                />
              ) : (
                ""
              )
            }
            tone="crit"
          />
        </Col>
        <Col md sm="6">
          <StatCard
            labelId="DailyAnalysisClientErrors"
            value={formatNumber(summary.clientErrors)}
            note={
              typeof summary.affectedUsers === "number" ? (
                <FormattedMessage
                  id="DailyAnalysisAffectedCount"
                  values={{ count: formatNumber(summary.affectedUsers) }}
                />
              ) : (
                ""
              )
            }
          />
        </Col>
      </Row>

      <Row className="da-equal">
        {activeUsers ? (
          <Col md="6">
            <PanelCard
              titleId="DailyAnalysisActiveUsers"
              subtitle={
                <FormattedMessage
                  id="DailyAnalysisActiveUsersSub"
                  values={{
                    asOf: formatDate(activeUsers.asOf, "d MMM HH:mm"),
                  }}
                />
              }
            >
              <Table responsive size="sm" className="da-table">
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="DailyAnalysisUserType" />
                    </th>
                    <th className="da-num">
                      <FormattedMessage id="DailyAnalysisActiveCount" />
                    </th>
                    <th className="da-num">
                      <FormattedMessage id="DailyAnalysisTotalCount" />
                    </th>
                    <th className="da-num">
                      <FormattedMessage id="DailyAnalysisNeverSeen" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>
                        <FormattedMessage id="DailyAnalysisAllUsers" />
                      </strong>
                    </td>
                    <td className="da-num da-num--strong">
                      {formatNumber(allUsers.active)}
                    </td>
                    <td className="da-num da-num--strong">
                      {formatNumber(allUsers.total)}
                    </td>
                    <td className="da-num da-num--strong">
                      {formatNumber(allUsers.neverSeen)}
                    </td>
                  </tr>
                  {activeUserRows.map((type) => (
                    <tr key={type.userType}>
                      <td>
                        <FormattedMessage id={type.userType} />
                      </td>
                      <td className="da-num da-num--strong">
                        {formatNumber(type.active)}
                      </td>
                      <td className="da-num">{formatNumber(type.total)}</td>
                      <td className="da-num">{formatNumber(type.neverSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        {showDailyTrend ? (
          <Col md="6">
            <PanelCard
              titleId="DailyAnalysisDailyActive"
              subtitle={<FormattedMessage id="DailyAnalysisDailyActiveSub" />}
            >
              <div className="da-trendlegend">
                {trendSeries.map((series) => (
                  <span className="da-trendlegend__item" key={series.id}>
                    <span
                      className={`da-trendlegend__swatch da-trendlegend__swatch--${series.colorIndex}`}
                    />
                    {series.name}
                  </span>
                ))}
              </div>
              <TrendLine
                labels={trendLabels}
                series={trendSeries}
                labelEvery={trendLabels.length > 12 ? 2 : 1}
              />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}
      </Row>

      <Row className="da-equal">
        {days.length > 0 ? (
          <Col md="6">
            <PanelCard
              titleId="DailyAnalysisRequestsPerDay"
              subtitle={
                changeFromPrevious === null ? (
                  ""
                ) : (
                  <FormattedMessage
                    id="DailyAnalysisVsPrevious"
                    values={{
                      change: `${changeFromPrevious > 0 ? "+" : ""}${changeFromPrevious.toLocaleString(
                        "sv-SE",
                        { maximumFractionDigits: 1 },
                      )}`,
                      date: formatDate(summary.requestsPreviousDate, "d MMM"),
                    }}
                  />
                )
              }
            >
              <DayBars points={days} />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        {moduleStartDays.length > 0 ? (
          <Col md="6">
            <PanelCard titleId="DailyAnalysisModuleStartsPerDay">
              <DayBars points={moduleStartDays} />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}
      </Row>

      <Row className="da-equal">
        {hours.length > 0 ? (
          <Col md="4">
            <PanelCard
              titleId="DailyAnalysisPerHour"
              subtitle={
                peakHour ? (
                  <FormattedMessage
                    id="DailyAnalysisPeakHour"
                    values={{
                      hour: peakHour.label,
                      count: formatNumber(peakHour.count),
                    }}
                  />
                ) : (
                  ""
                )
              }
            >
              <DayBars points={hours} labelEvery={6} />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        {moduleStartHours.length > 0 ? (
          <Col md="4">
            <PanelCard
              titleId="DailyAnalysisModuleStartsPerHour"
              subtitle={
                moduleStartPeakHour.count > 0 ? (
                  <FormattedMessage
                    id="DailyAnalysisModuleStartsPeakHour"
                    values={{
                      hour: moduleStartPeakHour.label,
                      count: formatNumber(moduleStartPeakHour.count),
                    }}
                  />
                ) : (
                  ""
                )
              }
            >
              <DayBars points={moduleStartHours} labelEvery={6} />
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        {split ? (
          <Col md="4">
            <PanelCard
              titleId="DailyAnalysisTrafficSplit"
              subtitle={<FormattedMessage id="DailyAnalysisTrafficSplitSub" />}
            >
              {split.map((row) => (
                <BarRow
                  key={row.id}
                  label={<FormattedMessage id={row.labelId} />}
                  value={row.value}
                  max={splitMax}
                  display={`${row.value.toLocaleString("sv-SE", {
                    maximumFractionDigits: 1,
                  })} %`}
                />
              ))}
            </PanelCard>
          </Col>
        ) : (
          ""
        )}
      </Row>

      <Row className="da-equal">
        {data.themes && data.themes.length > 0 ? (
          <Col md="6">
            <PanelCard
              titleId="DailyAnalysisThemes"
              subtitle={<FormattedMessage id="DailyAnalysisThemesSub" />}
            >
              <ul className="da-themes">
                {data.themes.map((theme, i) => (
                  <li key={i}>{theme}</li>
                ))}
              </ul>
            </PanelCard>
          </Col>
        ) : (
          ""
        )}

        <Col md="6">
          <PanelCard
            titleId="DailyAnalysisWhereProblems"
            subtitle={<FormattedMessage id="DailyAnalysisWhereProblemsSub" />}
          >
            <Table responsive size="sm" className="da-table">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id="DailyAnalysisSource" />
                  </th>
                  <th className="da-num">
                    <FormattedMessage id="DailyAnalysisCritical" />
                  </th>
                  <th className="da-num">
                    <FormattedMessage id="DailyAnalysisWarnings" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {perTab.map((tab) => (
                  <tr key={tab.id}>
                    <td>
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => onSelectTab(tab.id)}
                      >
                        <FormattedMessage id={tab.labelId} />
                      </Button>
                    </td>
                    <td
                      className={
                        tab.crit > 0
                          ? "da-num da-num--strong da-num--crit"
                          : "da-num"
                      }
                    >
                      {formatNumber(tab.crit)}
                    </td>
                    <td className="da-num">{formatNumber(tab.warn)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PanelCard>
        </Col>
      </Row>

      {/* What the page knows and how. appLogOrigin says "events" while lwc-logtypes is
          undeployed, which is why the application-log numbers are grouped locally. */}
      <p className="text-muted small mt-4">
        <FormattedMessage
          id="DailyAnalysisGenerated"
          values={{
            when: formatDate(data.generatedAt, "d MMM HH:mm"),
            source: data.appLogOrigin,
            days: data.baselineDays,
          }}
        />
      </p>
    </div>
  );
}

export default DailyAnalysisOverview;
